<template>
  <div class="register-admin-workspace">
    <aside class="register-admin-workspace__rail" aria-label="Register workspace navigation">
      <div class="register-admin-workspace__rail-header">
        <div class="register-admin-workspace__rail-mark" aria-hidden="true">
          <v-icon color="secondary" size="22">mdi-table-large</v-icon>
        </div>
        <div>
          <strong>Register workspace</strong>
          <span>Data and connections</span>
        </div>
      </div>

      <div class="register-admin-workspace__scope">
        <v-icon color="secondary" size="18">{{ scopeIcon }}</v-icon>
        <div>
          <span>{{ scopeKindLabel }}</span>
          <strong :title="scope.label || 'Not assigned'">{{ scope.label || 'Not assigned' }}</strong>
          <small v-if="scopeModeLine" class="register-admin-workspace__scope-mode">{{ scopeModeLine }}</small>
        </div>
      </div>

      <nav class="register-admin-workspace__nav" aria-label="Register workspace views">
        <button
          v-for="view in workspaceNavItems"
          :key="view.id"
          type="button"
          class="register-admin-workspace__nav-item"
          :class="{ 'register-admin-workspace__nav-item--active': activeView === view.id }"
          :aria-current="activeView === view.id ? 'page' : null"
          @click="selectView(view.id)">
          <v-icon size="20">{{ view.icon }}</v-icon>
          <span>
            <strong>{{ view.label }}</strong>
            <small>{{ view.description }}</small>
          </span>
          <span class="register-admin-workspace__nav-count">{{ railCountLabel(view.count) }}</span>
        </button>
      </nav>

      <div class="register-admin-workspace__rail-status" aria-label="Register workspace status">
        <strong class="register-admin-workspace__rail-status-title">At a glance</strong>
        <div>
          <v-icon color="secondary" size="17">mdi-format-list-checks</v-icon>
          <span>
            <strong>{{ selectionLabel }}</strong>
            <small>Selection stays while you switch views</small>
          </span>
        </div>
        <div>
          <v-icon color="secondary" size="17">mdi-link-variant</v-icon>
          <span>
            <strong>{{ activeLabel }}</strong>
            <small>{{ totalRelationshipsLabel }}</small>
          </span>
        </div>
        <div>
          <v-icon :color="reviewCount ? 'warning' : 'secondary'" size="17">
            {{ reviewCount ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
          </v-icon>
          <span>
            <strong>{{ reviewLabel }}</strong>
            <small>{{ previousRunsLabel }}</small>
          </span>
        </div>
      </div>
    </aside>

    <main class="register-admin-workspace__pane">
      <section v-show="activeView === 'data'" class="register-admin-workspace__view" aria-label="Register data">
        <register-table
          ref="registerTable"
          :current-register-type="currentRegisterType"
          :register-id="register.id"
          :facility-id="facilityId"
          :register-info="register"
          view-context="central"
          :initial-selected-row-ids="selectedRowIds"
          :relationship-attention-filter="relationshipAttentionFilter"
          :key="`register-table-${register.id}`"
          @selection-change="onSelectionChange"
          @filter-snapshot-change="onFilterSnapshotChange"
          @clear-relationship-attention-filter="clearRelationshipAttentionFilter"
          @showIn3d="$emit('showIn3d', $event)" />
      </section>

      <section
        v-if="relationshipViewVisited"
        v-show="activeView !== 'data'"
        class="register-admin-workspace__view"
        aria-label="Register relationships">
        <relationships-tab
          :register="register"
          :selected-row-ids="selectedRowIds"
          :data-filter-snapshot="dataFilterSnapshot"
          :active-view="activeView"
          @run-refresh-cancelled="onRelationshipRunRefreshCancelled"
          @run-refresh-pending="onRelationshipRunRefreshPending"
          @run-terminal="onRelationshipRunTerminal"
          @show-attention-rows="showAttentionRows" />
      </section>
    </main>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import RegisterTable from '../../views/RegisterTable.vue';
import RelationshipsTab from '../relationships/RelationshipsTab.vue';
import {
  RELATIONSHIP_ATTENTION_CLASSIFICATION,
  registerOwnershipScope,
  relationshipNeedsAttention,
} from '../../config/relationshipUi';

function emptyDataFilterSnapshot() {
  return {
    generation: 0,
    requestId: null,
    status: 'idle',
    search: '',
    columnFilters: {},
    totalCount: null,
    hasFilters: false,
  };
}

export default {
  name: 'RegisterAdminWorkspace',
  components: { RegisterTable, RelationshipsTab },
  props: {
    register: { type: Object, required: true },
    currentRegisterType: { type: String, required: true },
    initialTab: { type: String, default: 'data' },
  },
  data() {
    const activeView = ['relationships', 'history'].includes(this.initialTab)
      ? this.initialTab
      : 'data';
    return {
      activeView,
      relationshipViewVisited: activeView !== 'data',
      selectedRowIds: [],
      dataFilterSnapshot: emptyDataFilterSnapshot(),
      relationshipAttentionFilter: null,
      pendingRelationshipRunId: null,
      dataRefreshRequested: 0,
      dataRefreshCompleted: 0,
      dataRefreshInFlight: false,
      dataRefreshGeneration: 0,
      terminalDataRefreshRunIds: [],
    };
  },
  computed: {
    ...mapState('REGISTERS/relationships', [
      'availability',
      'historyAvailability',
      'relationships',
      'relationshipsTotal',
      'runHistory',
      'runHistoryTotal',
    ]),
    facilityId() {
      return this.register.facilityId || this.register.facility?.id || null;
    },
    workspaceIdentity() {
      const groupId = this.register.facilityGroupId || this.register.facilityGroup?.id || '';
      return `${this.register.id || ''}:${this.facilityId || ''}:${groupId}`;
    },
    scope() {
      return registerOwnershipScope(this.register);
    },
    scopeKindLabel() {
      if (this.scope.kind === 'facilityGroup') return 'Facility Group';
      if (this.scope.kind === 'facility') return 'Facility';
      return 'Register owner';
    },
    // Facility Group registers: which sharing mode the rows follow (showcase B rail).
    scopeModeLine() {
      if (this.scope.kind !== 'facilityGroup'
        || !Object.prototype.hasOwnProperty.call(this.register, 'facilityAssignmentMode')) return '';
      const mode = this.register.facilityAssignmentMode;
      if (mode === 'Shared') return 'Shared · all member Facilities';
      if (mode === 'Separated') return 'Separated · one Facility per row';
      return 'Mode not set';
    },
    scopeIcon() {
      if (this.scope.kind === 'facilityGroup') return 'mdi-domain';
      if (this.scope.kind === 'facility') return 'mdi-map-marker-outline';
      return 'mdi-map-marker-question-outline';
    },
    // An empty store before the workspace has answered is not a count of zero. These stay null so
    // railCountLabel renders its placeholder instead of asserting a number nobody has established.
    relationshipsCounted() {
      return this.relationshipViewVisited && this.availability === 'ready';
    },
    relationshipTotalCount() {
      if (!this.relationshipsCounted) return null;
      return Number.isInteger(this.relationshipsTotal)
        ? this.relationshipsTotal
        : this.relationships.length;
    },
    runHistoryTotalCount() {
      if (!this.relationshipViewVisited || this.historyAvailability !== 'ready') return null;
      return Number.isInteger(this.runHistoryTotal)
        ? this.runHistoryTotal
        : this.runHistory.length;
    },
    enabledRelationshipsCount() {
      if (!this.relationshipsCounted) return null;
      return this.relationships.filter((relationship) => relationship?.isEnabled === true).length;
    },
    // Same predicate, same unit, same wording as the Relationships tab strip and each card's review
    // block, so no surface can flag a relationship while another claims nothing needs review.
    reviewCount() {
      if (!this.relationshipsCounted
        || ['unknown', 'loading'].includes(this.historyAvailability)) return null;
      return this.relationships
        .filter((relationship) => relationshipNeedsAttention(relationship, this.runHistory)).length;
    },
    // Zero states are worded so no caption in this block starts with a digit while its siblings
    // start with a letter. A count that is not established yet keeps the placeholder.
    selectionLabel() {
      const count = this.selectedRowIds.length;
      if (!count) return 'No rows selected';
      return `${count} ${count === 1 ? 'row' : 'rows'} selected`;
    },
    activeLabel() {
      const count = this.enabledRelationshipsCount;
      if (count === null) return '— active';
      return count ? `${count} active` : 'None active';
    },
    totalRelationshipsLabel() {
      const count = this.relationshipTotalCount;
      if (count === null) return '— total relationships';
      if (!count) return 'No relationships yet';
      return `${count} total ${count === 1 ? 'relationship' : 'relationships'}`;
    },
    previousRunsLabel() {
      const count = this.runHistoryTotalCount;
      if (count === null) return '— previous runs';
      if (!count) return 'No previous runs';
      return `${count} previous ${count === 1 ? 'run' : 'runs'}`;
    },
    reviewLabel() {
      if (this.reviewCount === null) return '— need review';
      if (!this.reviewCount) return 'Nothing needs review';
      return `${this.reviewCount} ${this.reviewCount === 1 ? 'needs' : 'need'} review`;
    },
    workspaceNavItems() {
      const items = [{
        id: 'data',
        label: 'Data',
        description: 'Rows and fields',
        icon: 'mdi-table-large',
        count: this.dataFilterSnapshot.totalCount,
      }];

      items.push(
        {
          id: 'relationships',
          label: 'Relationships',
          description: 'Status and actions',
          icon: 'mdi-vector-link',
          count: this.relationshipTotalCount,
        },
        {
          id: 'history',
          label: 'Run history',
          description: 'Previous activity',
          icon: 'mdi-history',
          count: this.runHistoryTotalCount,
        },
      );

      return items;
    },
  },
  watch: {
    initialTab(value) {
      this.selectView(value);
    },
    'register.id'() {
      this.resetDataRefreshState();
      this.selectedRowIds = [];
      this.dataFilterSnapshot = emptyDataFilterSnapshot();
      this.relationshipAttentionFilter = null;
      this.activeView = this.normalizeView(this.initialTab);
      this.relationshipViewVisited = this.activeView !== 'data';
    },
    workspaceIdentity(value, oldValue) {
      if (oldValue === undefined || value === oldValue) return;
      this.resetDataRefreshState();
      this.selectedRowIds = [];
      this.dataFilterSnapshot = emptyDataFilterSnapshot();
      this.relationshipAttentionFilter = null;
    },
  },
  methods: {
    normalizeView(value) {
      return ['relationships', 'history'].includes(value) ? value : 'data';
    },
    selectView(view) {
      const next = this.normalizeView(view);
      if (next !== 'data') this.relationshipViewVisited = true;
      this.activeView = next;
      if (next === 'data') this.$nextTick(() => this.refreshDataIfNeeded());
    },
    railCountLabel(value) {
      return Number.isInteger(value) ? value : '—';
    },
    onSelectionChange(rowIds) {
      const next = [...new Set(rowIds || [])];
      const unchanged = next.length === this.selectedRowIds.length
        && next.every(id => this.selectedRowIds.includes(id));
      if (!unchanged) this.selectedRowIds = next;
    },
    onFilterSnapshotChange(snapshot) {
      const generation = Number(snapshot?.generation);
      if (!Number.isInteger(generation) || generation < 0) return;
      if (generation < this.dataFilterSnapshot.generation) return;
      if (generation === this.dataFilterSnapshot.generation
        && this.dataFilterSnapshot.requestId
        && snapshot?.requestId !== this.dataFilterSnapshot.requestId) return;

      const search = typeof snapshot?.search === 'string' ? snapshot.search.trim() : '';
      const columnFilters = snapshot?.columnFilters && typeof snapshot.columnFilters === 'object'
        && !Array.isArray(snapshot.columnFilters)
        ? { ...snapshot.columnFilters }
        : {};
      const hasFilters = Boolean(search || Object.keys(columnFilters).length);
      const hasTotalCount = snapshot?.totalCount !== null
        && snapshot?.totalCount !== undefined
        && snapshot?.totalCount !== '';
      const totalCount = hasTotalCount ? Number(snapshot.totalCount) : null;
      this.dataFilterSnapshot = {
        generation,
        requestId: snapshot?.requestId || `data-${generation}`,
        status: ['idle', 'loading', 'ready', 'error'].includes(snapshot?.status)
          ? snapshot.status
          : 'error',
        search,
        columnFilters,
        totalCount: Number.isInteger(totalCount) && totalCount >= 0 ? totalCount : null,
        hasFilters,
      };
    },
    showAttentionRows(filter) {
      if (typeof filter?.relationshipRunId !== 'string'
        || !filter.relationshipRunId.trim()
        || filter.classification !== RELATIONSHIP_ATTENTION_CLASSIFICATION) return;
      this.relationshipAttentionFilter = {
        relationshipRunId: filter.relationshipRunId,
        classification: RELATIONSHIP_ATTENTION_CLASSIFICATION,
      };
      this.selectedRowIds = [];
      this.activeView = 'data';
      this.$nextTick(() => this.refreshDataIfNeeded());
    },
    clearRelationshipAttentionFilter() {
      this.relationshipAttentionFilter = null;
    },
    resetDataRefreshState() {
      this.dataRefreshGeneration += 1;
      this.dataRefreshRequested = 0;
      this.dataRefreshCompleted = 0;
      this.dataRefreshInFlight = false;
      this.pendingRelationshipRunId = null;
      this.terminalDataRefreshRunIds = [];
    },
    requestDataRefresh() {
      this.dataRefreshRequested += 1;
      if (this.activeView === 'data') this.$nextTick(() => this.refreshDataIfNeeded());
    },
    onRelationshipRunRefreshPending(event) {
      if (typeof event?.runId !== 'string' || !event.runId.trim()) return;
      this.pendingRelationshipRunId = event.runId;
    },
    onRelationshipRunRefreshCancelled(event) {
      if (event?.runId === this.pendingRelationshipRunId) this.pendingRelationshipRunId = null;
    },
    onRelationshipRunTerminal(run) {
      if (!run?.id) return;
      if (this.terminalDataRefreshRunIds.includes(run.id)) return;
      this.terminalDataRefreshRunIds = [...this.terminalDataRefreshRunIds, run.id];
      if (this.relationshipAttentionFilter?.relationshipRunId
        && this.relationshipAttentionFilter.relationshipRunId !== run.id) {
        this.relationshipAttentionFilter = null;
      }
      if (this.pendingRelationshipRunId === run.id) this.pendingRelationshipRunId = null;
      this.requestDataRefresh();
    },
    async refreshDataIfNeeded() {
      if (this.activeView !== 'data'
        || this.dataRefreshInFlight
        || this.dataRefreshCompleted >= this.dataRefreshRequested) return;

      const generation = this.dataRefreshGeneration;
      const identity = this.workspaceIdentity;
      const requested = this.dataRefreshRequested;
      let refreshed = false;
      this.dataRefreshInFlight = true;
      await this.$nextTick();
      try {
        const table = this.$refs.registerTable;
        if (!table || typeof table.loadTableData !== 'function') return;
        await table.loadTableData();
        refreshed = true;
        if (generation === this.dataRefreshGeneration && identity === this.workspaceIdentity) {
          this.dataRefreshCompleted = Math.max(this.dataRefreshCompleted, requested);
        }
      } finally {
        if (generation === this.dataRefreshGeneration && identity === this.workspaceIdentity) {
          this.dataRefreshInFlight = false;
          if (refreshed
            && this.activeView === 'data'
            && this.dataRefreshCompleted < this.dataRefreshRequested) {
            this.refreshDataIfNeeded();
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register-admin-workspace {
  --workspace-muted: #59635f;
  display: grid;
  height: 100%;
  min-width: 0;
  grid-template-columns: 252px minmax(0, 1fr);
  overflow: hidden;
  color: #202825;
  background: var(--v-bg1-base);
  font-family: var(--ds-font-family-body);
}
.register-admin-workspace__rail { display: flex; min-width: 0; min-height: 0; flex-direction: column; overflow: hidden; color: #fff; background: #082824; }
.register-admin-workspace__rail-header { display: flex; align-items: center; flex: 0 0 auto; gap: var(--ds-space-sm); min-height: 78px; padding: var(--ds-space-md); border-bottom: 1px solid rgba(255, 255, 255, 0.13); }
.register-admin-workspace__rail-mark { display: flex; align-items: center; justify-content: center; width: 42px; height: 42px; flex: 0 0 42px; border-radius: var(--ds-radius-md); background: rgba(255, 255, 255, 0.12); }
.register-admin-workspace__rail-header > div:last-child { display: flex; min-width: 0; flex-direction: column; }
.register-admin-workspace__rail-header strong { font-family: var(--ds-font-family-heading); font-size: var(--ds-font-body-sm); font-weight: 600; }
.register-admin-workspace__rail-header span { color: rgba(255, 255, 255, 0.76); font-size: var(--ds-font-caption); }
.register-admin-workspace__scope { display: flex; align-items: flex-start; flex: 0 0 auto; gap: var(--ds-space-sm); margin: var(--ds-space-md); padding: var(--ds-space-sm); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: var(--ds-radius-md); background: rgba(255, 255, 255, 0.06); }
.register-admin-workspace__scope > div { display: flex; min-width: 0; flex-direction: column; }
.register-admin-workspace__scope span { color: rgba(255, 255, 255, 0.76); font-size: var(--ds-font-caption); }
.register-admin-workspace__scope strong { overflow: hidden; color: #fff; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-body-sm); font-weight: 600; }
.register-admin-workspace__scope .register-admin-workspace__scope-mode { margin-top: 4px; color: rgba(255, 255, 255, 0.86); font-size: 11px; }
.register-admin-workspace__nav { display: flex; flex: 0 0 auto; flex-direction: column; gap: 4px; padding: 0 var(--ds-space-sm); }
/* Same idiom as the main app nav (ExpandableRail group rows): rounded tonal fill for the active view,
   stronger title, accent icon. No left border accent. */
.register-admin-workspace__nav-item { display: grid; width: auto; min-height: 58px; grid-template-columns: 24px minmax(0, 1fr) auto; align-items: center; gap: var(--ds-space-sm); margin: 0 4px; padding: 8px 12px; border: 0; border-radius: var(--ds-radius-md); color: rgba(255, 255, 255, 0.82); background: transparent; text-align: left; cursor: pointer; transition: background-color 120ms ease; }
.register-admin-workspace__nav-item:hover { background: rgba(255, 255, 255, 0.06); }
.register-admin-workspace__nav-item:focus-visible { outline: 2px solid var(--v-secondary-base); outline-offset: 2px; }
.register-admin-workspace__nav-item > .v-icon { color: rgba(255, 255, 255, 0.82) !important; }
.register-admin-workspace__nav-item > span:nth-child(2) { display: flex; min-width: 0; flex-direction: column; }
.register-admin-workspace__nav-item strong { overflow: hidden; color: inherit; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-body-sm); font-weight: 500; }
.register-admin-workspace__nav-item small { overflow: hidden; color: rgba(255, 255, 255, 0.76); text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-caption); }
.register-admin-workspace__nav-item--active { color: #fff; background: rgba(44, 194, 149, 0.16); }
.register-admin-workspace__nav-item--active:hover { background: rgba(44, 194, 149, 0.2); }
.register-admin-workspace__nav-item--active strong { color: #fff; font-weight: 600; }
.register-admin-workspace__nav-item--active > .v-icon { color: var(--v-secondary-base) !important; }
.register-admin-workspace__nav-item--active .register-admin-workspace__nav-count { background: rgba(44, 194, 149, 0.28); }
.register-admin-workspace__nav-item--active small { color: rgba(255, 255, 255, 0.86); }
.register-admin-workspace__nav-count { min-width: 28px; padding: 2px 6px; border-radius: 999px; color: #fff; background: rgba(255, 255, 255, 0.14); text-align: center; font-size: 11px; font-weight: 700; }
.register-admin-workspace__rail-status { display: flex; min-height: 0; flex: 1; flex-direction: column; justify-content: flex-end; gap: var(--ds-space-sm); padding: var(--ds-space-md); border-top: 1px solid rgba(255, 255, 255, 0.13); }
.register-admin-workspace__rail-status-title { color: rgba(255, 255, 255, 0.76); font-size: var(--ds-font-caption); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.register-admin-workspace__rail-status > div { display: flex; align-items: flex-start; gap: var(--ds-space-sm); }
.register-admin-workspace__rail-status > div > span { display: flex; min-width: 0; flex-direction: column; }
.register-admin-workspace__rail-status strong { color: #fff; font-size: var(--ds-font-caption); font-weight: 600; }
.register-admin-workspace__rail-status small { color: rgba(255, 255, 255, 0.76); font-size: 11px; line-height: 1.35; }
.register-admin-workspace__pane { min-width: 0; min-height: 0; overflow: hidden; background: var(--v-bg1-base); }
.register-admin-workspace__view { height: 100%; min-width: 0; min-height: 0; overflow: hidden; }

@media (max-width: 1100px) {
  .register-admin-workspace { grid-template-columns: 228px minmax(0, 1fr); }
  .register-admin-workspace__scope { margin: var(--ds-space-sm); }
  .register-admin-workspace__rail-header, .register-admin-workspace__rail-status { padding-right: var(--ds-space-sm); padding-left: var(--ds-space-sm); }
}

@media (prefers-reduced-motion: reduce) {
  .register-admin-workspace__nav-item { transition-duration: 0.01ms !important; }
}
</style>
